import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Phone, MessageSquare, Copy, Check, Smartphone,
} from "lucide-react";

/**
 * NativePhoneDialog
 * Shows the target phone number BIG, a Copy button, a QR code (scan on phone
 * to auto-open dialer/messages), and a "Open now on this device" button that
 * fires tel:/sms: as a normal href.
 *
 * Props:
 *   open, onOpenChange   — controlled Dialog state
 *   phone                — E.164 phone number to dial/text
 *   name                 — optional contact display name
 *   mode                 — "call" | "sms"
 *   leadId               — optional lead id for activity logging
 *   dnc                  — optional bool — shows a DNC warning banner
 *   onCallOutcome        — optional (fn) called with no args once the user
 *                          taps "Dial on this device" — used to open the
 *                          post-call outcome prompt in the parent
 */
export default function NativePhoneDialog({
  open, onOpenChange, phone, name, mode = "call", leadId = null,
  dnc = false, onCallOutcome = null,
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (!open) setCopied(false); }, [open]);

  if (!phone) return null;
  const isCall = mode === "call";
  const target = isCall ? `tel:${phone}` : `sms:${phone}`;
  const qrData = encodeURIComponent(target);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=6&data=${qrData}`;
  const Icon = isCall ? Phone : MessageSquare;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      toast.success("Number copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy — long-press the number to copy manually");
    }
  };

  const logAndOpen = () => {
    if (leadId) {
      api.post("/twilio/log-manual", {
        lead_id: leadId, phone, kind: isCall ? "call" : "sms",
      }).catch(() => {});
    }
    // Fire the outcome prompt only for calls, after the tel: navigation
    if (isCall && onCallOutcome) {
      // Delay so tel: has time to fire before we open another modal
      setTimeout(() => {
        onOpenChange(false);
        onCallOutcome();
      }, 250);
    }
    // Let the native <a href> handle navigation
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="native-phone-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Icon size={18} className={isCall ? "text-emerald-600" : "text-blue-600"} />
            {isCall ? "Call" : "Text"} {name || "this contact"}
          </DialogTitle>
          <DialogDescription>
            {isCall
              ? "Dial from your own phone — no Twilio bridge needed."
              : "Text from your own phone — no Twilio SMS setup needed."}
          </DialogDescription>
        </DialogHeader>

        {dnc && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 flex items-start gap-2"
            data-testid="native-dnc-warning"
          >
            <span className="text-red-600 font-bold">DNC</span>
            <span>
              This lead is marked <strong>Do Not Call</strong>. Contacting them
              may violate their opt-out request. Proceed only if you have
              explicit permission.
            </span>
          </div>
        )}

        {/* Big number */}
        <div className="border border-[var(--app-line)] rounded-xl p-5 text-center bg-[var(--app-surface)]/40">
          <div className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-1">
            Number to {isCall ? "dial" : "text"}
          </div>
          <div
            className="font-mono text-2xl md:text-3xl font-bold tracking-wider select-all break-all"
            data-testid="native-phone-number"
          >
            {phone}
          </div>
          <button
            onClick={copy}
            className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[var(--app-line)] rounded-full hover:bg-white transition-colors"
            data-testid="native-copy-btn"
          >
            {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
            {copied ? "Copied" : "Copy number"}
          </button>
        </div>

        {/* QR + phone-native button */}
        <div className="grid grid-cols-2 gap-3 items-center">
          <div className="flex flex-col items-center">
            <img
              src={qrSrc}
              alt="Scan to dial"
              className="w-32 h-32 border border-[var(--app-line)] rounded-lg bg-white p-1"
              data-testid="native-qr-code"
            />
            <div className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mt-2 text-center">
              <Smartphone size={11} className="inline mb-0.5 mr-1" />
              Scan with your phone
            </div>
          </div>
          <div className="space-y-2">
            <a
              href={target}
              onClick={logAndOpen}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)] transition-colors text-sm font-medium"
              data-testid="native-open-btn"
            >
              <Icon size={14} />
              {isCall ? "Dial on this device" : "Message on this device"}
            </a>
            <p className="text-[11px] text-[var(--app-text-muted)] leading-snug">
              On mobile this opens your dialer / Messages instantly. On desktop
              your OS may ask which app to open — scan the QR with your phone
              instead.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
